import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeAdminThemeSettings } from '@/lib/admin-theme-settings';
import type { AiQuoteDraft } from '@/lib/ai-quote-draft';
import { processLogoImage } from '@/lib/logo-image';
import { checkLogoDataUrl } from '../../supabase/functions/_shared/logo-image-data';
import { sanitizeSvg } from '../../supabase/functions/_shared/svg-sanitize';

/**
 * AI proposal writer and AI logo maker. Both edge functions answer friendly
 * outcomes (upgrade needed, not switched on yet, daily limit, refusal) as
 * { ok: false, code, message } so the dialogs can show them in place.
 */

export type AiFailure = { ok: false; code: string; message: string; configured?: boolean };
export type AiOutcome<T> = ({ ok: true } & T) | AiFailure;

/** supabase-js puts the JSON body of a non-2xx answer in error.context. */
async function invokeAi<T>(name: string, body: Record<string, unknown>): Promise<AiOutcome<T>> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === 'function') {
      try {
        const json = await context.json();
        if (json && typeof json === 'object') {
          const message = String(json.message ?? json.error ?? '');
          if (message) return { ok: false, code: String(json.code ?? 'error'), message };
        }
      } catch {
        // fall through to the generic message
      }
    }
    return { ok: false, code: 'network', message: 'We couldn’t reach the AI helper. Please try again.' };
  }
  if (!data || typeof data !== 'object') {
    return { ok: false, code: 'error', message: 'Something went wrong. Please try again.' };
  }
  return data as AiOutcome<T>;
}

// --- Proposal writer --------------------------------------------------------------

export interface AiDraftQuoteRequest {
  organizationId: string;
  details: string;
  customerName?: string;
  frequency?: string;
}

export function useAiDraftQuote() {
  return useMutation({
    mutationFn: (input: AiDraftQuoteRequest) =>
      invokeAi<{ draft: AiQuoteDraft }>('ai-draft-quote', {
        organizationId: input.organizationId,
        details: input.details,
        customerName: input.customerName ?? '',
        frequency: input.frequency ?? '',
      }),
  });
}

// --- Logo maker -------------------------------------------------------------------

export type AiLogoStyle = 'modern' | 'friendly' | 'premium' | 'minimal';

/** A picture logo (the default mode), already made transparent and trimmed in the browser. */
export interface AiImageLogoOption {
  kind: 'image';
  name: string;
  concept: string;
  /** Transparent PNG data URL, for previews. */
  image: string;
  /** The same PNG, for upload. */
  png: Blob;
}

/** An SVG logo (AI_LOGO_MODE=svg on the function). */
export interface AiSvgLogoOption {
  kind: 'svg';
  name: string;
  concept: string;
  wide_svg: string;
  square_svg: string | null;
}

export type AiLogoOption = AiImageLogoOption | AiSvgLogoOption;

/** What the function sends: one of the two shapes per option. */
interface RawLogoOption {
  name?: unknown;
  concept?: unknown;
  image?: unknown;
  wide_svg?: unknown;
  square_svg?: unknown;
}

export interface AiGenerateLogoRequest {
  organizationId: string;
  businessName: string;
  style: AiLogoStyle;
  colours: string[];
  iconIdea?: string;
  avoid?: string[];
}

/**
 * Check and prepare one option from the function. SVGs are sanitised again;
 * pictures are re-checked (PNG/JPEG/WebP data URL, size cap), then their white
 * background is removed. Anything that fails is dropped (null).
 */
async function prepareLogoOption(logo: RawLogoOption): Promise<AiLogoOption | null> {
  const name = String(logo.name ?? '');
  const concept = String(logo.concept ?? '');
  if (typeof logo.image === 'string') {
    if (!checkLogoDataUrl(logo.image).ok) return null;
    try {
      const processed = await processLogoImage(logo.image);
      return { kind: 'image', name, concept, image: processed.dataUrl, png: processed.png };
    } catch {
      return null;
    }
  }
  const wide = sanitizeSvg(logo.wide_svg);
  if (!wide.ok) return null;
  const square = typeof logo.square_svg === 'string' && logo.square_svg ? sanitizeSvg(logo.square_svg) : null;
  return { kind: 'svg', name, concept, wide_svg: wide.svg, square_svg: square && square.ok ? square.svg : null };
}

export function useAiGenerateLogo() {
  return useMutation({
    mutationFn: async (input: AiGenerateLogoRequest) => {
      const result = await invokeAi<{ logos: RawLogoOption[] }>('ai-generate-logo', { ...input });
      if (!result.ok) return result as AiFailure;
      // Belt and braces: the function already checked these. Anything that
      // doesn't pass again in the browser is dropped before it is previewed.
      const raw = (Array.isArray(result.logos) ? result.logos : []).slice(0, 3);
      const logos = (await Promise.all(raw.map((logo) => (logo && typeof logo === 'object' ? prepareLogoOption(logo) : null)))).filter(
        (logo): logo is AiLogoOption => logo !== null,
      );
      if (logos.length === 0) {
        return { ok: false as const, code: 'invalid_output', message: 'We couldn’t use those designs. Please try again.' };
      }
      return { ok: true as const, logos };
    },
  });
}

/** The site's primary colour (hex) from theme settings, to prefill the logo colours. */
export function useBrandPrimaryColour(orgId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['ai-logo-brand-colour', orgId],
    enabled: Boolean(orgId) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<string | null> => {
      try {
        const { themeSettings } = await invokeAdminThemeSettings({ action: 'fetch', orgId: orgId! });
        const colour = themeSettings?.primary_color ?? null;
        return colour && /^#[0-9a-f]{6}$/i.test(colour) ? colour.toLowerCase() : null;
      } catch {
        return null;
      }
    },
  });
}

export interface UploadAiLogoInput {
  orgId: string;
  /** The transparent PNG that becomes the logo. */
  png: Blob;
  /** SVG mode only: the vector original, kept alongside. */
  svg?: string | null;
  squareSvg?: string | null;
}

/**
 * Upload the chosen logo as logo.png (what the site and emails use), plus, for
 * SVG logos, logo.svg (and the square icon version) for later use. Returns the
 * PNG's public URL, which the page saves as company_info.logo_url.
 */
export function useUploadAiLogo() {
  return useMutation({
    mutationFn: async ({ orgId, svg, png, squareSvg }: UploadAiLogoInput): Promise<{ logoUrl: string; svgUrl: string | null }> => {
      if (png.type && png.type !== 'image/png') throw new Error('That logo couldn’t be saved. Please pick another.');
      const clean = svg ? sanitizeSvg(svg) : null;
      if (clean && !clean.ok) throw new Error('That logo couldn’t be saved. Please pick another.');
      const bucket = supabase.storage.from('content-images');
      const uploads: Array<Promise<{ error: Error | null }>> = [
        bucket.upload(`${orgId}/company/logo.png`, png, { upsert: true, contentType: 'image/png' }),
      ];
      if (clean?.ok) {
        const svgBlob = new Blob([clean.svg], { type: 'image/svg+xml' });
        uploads.push(bucket.upload(`${orgId}/company/logo.svg`, svgBlob, { upsert: true, contentType: 'image/svg+xml' }));
      }
      const square = clean?.ok && squareSvg ? sanitizeSvg(squareSvg) : null;
      if (square?.ok) {
        uploads.push(
          bucket.upload(`${orgId}/company/logo-square.svg`, new Blob([square.svg], { type: 'image/svg+xml' }), {
            upsert: true,
            contentType: 'image/svg+xml',
          }),
        );
      }
      const results = await Promise.all(uploads);
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
      const stamp = Date.now();
      return {
        logoUrl: `${bucket.getPublicUrl(`${orgId}/company/logo.png`).data.publicUrl}?t=${stamp}`,
        svgUrl: clean?.ok ? `${bucket.getPublicUrl(`${orgId}/company/logo.svg`).data.publicUrl}?t=${stamp}` : null,
      };
    },
  });
}
